import React from 'react'
import { Box, Button, Paper, Stack, TextField, Typography } from '@mui/material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import { ThemeApi } from '../../../api/themeApi'
import TextEditor from '../../Helpers/TextEditor'
import SectionSpacingFields from './shared/SectionSpacingFields'
import useTranslation from '../../../hooks/useTranslation'
import toast from '../../../utils/toast'

export default function ContentBlockSettings({ settings, sectionId, themeId, onChange }) {
  const { t } = useTranslation()

  const handleImageUpload = async (file) => {
    try {
      const formData = new FormData()
      formData.append('image', file)
      const res = await ThemeApi.uploadSectionImage(themeId, sectionId, formData)
      onChange({ ...settings, image: res.data.url })
    } catch (err) {
      console.error('Image upload failed:', err)
      toast.error(t('labels.themes.imageUploadFailed'))
    }
  }

  const handleTextChange = (content) => {
    onChange({ ...settings, text: content })
  }

  const removeImage = () => onChange({ ...settings, image: '' })

  return (
    <Stack spacing={3}>
      <SectionSpacingFields
        settings={settings}
        onChange={onChange}
      />

      <Box>
        <Typography variant="body2" sx={{ mb: 1 }}>
          {t('labels.themes.content')}
        </Typography>
        <TextEditor
          value={settings.text || ''}
          onChange={handleTextChange}
          name="contentBlock"
          rows={10}
        />
      </Box>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 2 }}>
          {t('labels.themes.sideImageOptional')}
        </Typography>

        {settings.image ? (
          <Box
            sx={{
              width: '100%',
              height: 180,
              borderRadius: 1.5,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
              mb: 2,
              backgroundColor: '#f8fafc',
            }}
          >
            <img
              src={settings.image}
              alt=""
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          </Box>
        ) : (
          <Box
            sx={{
              width: '100%',
              height: 180,
              borderRadius: 1.5,
              border: '1px dashed',
              borderColor: 'divider',
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'text.secondary',
              backgroundColor: '#f8fafc',
            }}
          >
            {t('labels.themes.noImageSelected')}
          </Box>
        )}

        <Stack direction="row" spacing={1}>
          <Button
            component="label"
            variant="outlined"
            fullWidth
            startIcon={<CloudUploadIcon />}
          >
            {settings.image ? t('labels.themes.replaceImage') : t('labels.themes.uploadImage')}
            <input
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={(e) => e.target.files?.[0] && handleImageUpload(e.target.files[0])}
            />
          </Button>

          <Button
            variant="outlined"
            color="error"
            disabled={!settings.image}
            onClick={removeImage}
          >
            {t('labels.themes.remove')}
          </Button>
        </Stack>

        {settings.image && (
          <TextField
            label={t('labels.themes.imageAlt')}
            size="small"
            fullWidth
            value={settings.imageAlt || ''}
            onChange={(e) => onChange({ ...settings, imageAlt: e.target.value })}
            helperText={t('labels.themes.imageAltHelp')}
            sx={{ mt: 2 }}
          />
        )}
      </Paper>
    </Stack>
  )
}
